import { Controller } from "@hotwired/stimulus"

// Asks before leaving a form with unsaved changes: its Cancel link, another page, the sidebar, or closing the tab.
// The state is compared, not edits counted, so changing a field back counts as unchanged. A page whose state is not
// in its form fields (the questionnaire builder) sets `reported` and announces it with "unsaved-changes:dirty" and
// "unsaved-changes:clean" events instead.
export default class extends Controller {
  static values = { message: String, reported: Boolean }

  connect() {
    this.saved = this.snapshot()
    this.reportedDirty = false
    this.submitting = false
    this.onSubmit = () => { this.submitting = true }
    // A save that fails leaves the changes unsaved; one that succeeds is the new saved state.
    this.onSubmitEnd = (event) => {
      this.submitting = false
      if (event.detail?.success) this.markClean()
    }
    this.onDirty = () => { this.reportedDirty = true }
    this.onClean = () => this.markClean()
    this.onBeforeUnload = (event) => {
      if (!this.dirty()) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.onBeforeVisit = (event) => {
      if (this.leaving || !this.dirty()) return
      event.preventDefault()
      this.confirmThen(() => window.Turbo.visit(event.detail.url))
    }
    // The sidebar loads pages into the frame this one sits in, which turbo:before-visit doesn't announce. Only a
    // request that would replace this page is held; frames inside it (a preview) load as before.
    this.onBeforeFetch = (event) => {
      const frame = event.target
      if (this.leaving || this.submitting || !(frame instanceof Element) || frame.tagName !== "TURBO-FRAME") return
      if (!frame.contains(this.element) || !this.dirty()) return
      event.preventDefault()
      this.confirmThen(() => event.detail.resume())
    }
    // The rich-text editor rewrites its hidden value once it starts; that is not a change.
    this.onEditorReady = () => setTimeout(() => { this.saved = this.snapshot() }, 0)
    this.element.addEventListener("lexxy:initialize", this.onEditorReady)
    this.element.addEventListener("submit", this.onSubmit)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.addEventListener("unsaved-changes:dirty", this.onDirty)
    this.element.addEventListener("unsaved-changes:clean", this.onClean)
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:before-fetch-request", this.onBeforeFetch)
  }

  disconnect() {
    this.element.removeEventListener("lexxy:initialize", this.onEditorReady)
    this.element.removeEventListener("submit", this.onSubmit)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.removeEventListener("unsaved-changes:dirty", this.onDirty)
    this.element.removeEventListener("unsaved-changes:clean", this.onClean)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:before-fetch-request", this.onBeforeFetch)
  }

  // data-action="unsaved-changes#leave" on a link
  leave(event) {
    if (!this.dirty()) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const href = event.currentTarget.href
    this.confirmThen(() => window.Turbo ? window.Turbo.visit(href) : (window.location.href = href))
  }

  async confirmThen(proceed) {
    if (window.boConfirm && !(await window.boConfirm(this.messageValue, "danger"))) {
      document.dispatchEvent(new CustomEvent("navigation-loader:cancel"))
      return
    }
    this.leaving = true
    proceed()
  }

  markClean() {
    this.reportedDirty = false
    this.saved = this.snapshot()
  }

  dirty() {
    if (this.submitting) return false
    return this.reportedValue ? this.reportedDirty : this.snapshot() !== this.saved
  }

  snapshot() {
    const form = this.element.tagName === "FORM" ? this.element : this.element.querySelector("form")
    if (!form) return ""
    return JSON.stringify([...new FormData(form).entries()].filter(([name]) => name !== "authenticity_token"))
  }
}
